import { Gift, Paperclip, Send } from 'lucide-react';
import React, { memo, useCallback, useContext, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';

import IceBreakerIconButton from '../../../components/IceBreaker/IceBreakerIconButton';
import { useConsumeCoinsHandler } from '../../../components/InsufficientCoinsDialog/useConsumeCoinsHandler';
import Config from '../../../config';
import { IUser, getBalance } from '../../../models/user/IUser';
import { ChatMessageTypes } from '../../../models/chat/ChatMessageType';
import ChatActionCreator from '../../../services/Chat/actions/ChatActionCreator';
import useTranslation from '../../../services/i18n/core/useTranslation';
import NotificationActionCreator from '../../../services/Notifications/actions/NotificationActionCreator';
import ThemeContext from 'theme/ThemeContext';
import ChatGiftComponent from './ChatGiftComponent';

export interface IChatInputProps {
    dialogId: string;
    userName: string;
    user?: IUser;
    partnerId: string;
    canSendIcebreaker?: boolean;
    onSend?(): void;
}

export const ChatInput = memo((props: IChatInputProps) => {
    const { dialogId, user, partnerId, userName, onSend, canSendIcebreaker } = props;
    const { type } = useContext(ThemeContext);
    const inputRef = useRef<HTMLInputElement | null>(null);
    const fileInputRef = useRef<HTMLInputElement | null>(null);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [inputValue, setInputValue] = useState('');
    const dispatch = useDispatch();
    const { CHAT_INTERFACE_SEND_A_MESSAGE } = useTranslation();

    const handleGiftsClick = useCallback((event: React.MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget), []);
    const handleGiftsClose = useCallback(() => setAnchorEl(null), []);

    const handleSend = useCallback(async () => {
        const trimmedValue = inputValue.trim();
        if (getBalance(user!) >= Config.SEND_MESSAGE_AMOUNT && trimmedValue.length > 0) {
            await dispatch(ChatActionCreator.sendMessage(dialogId, trimmedValue, ChatMessageTypes.MESSAGE, user!.Userid));
            onSend?.();
            setInputValue('');
            if (inputRef.current) {
                inputRef.current.value = '';
            }
        }
    }, [dialogId, dispatch, inputValue, onSend, user]);

    const handler = useConsumeCoinsHandler(handleSend, Config.SEND_MESSAGE_AMOUNT);

    const handleEnter = async (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter' && !e.ctrlKey && !e.shiftKey) {
            e.preventDefault();
            await handler();
        }
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        const maxFileSize = 8 * 1024 * 1024;
        const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];

        if (files && files.length > 0) {
            const file = files[0];

            if (!allowedTypes.includes(file.type)) {
                dispatch(
                    NotificationActionCreator.enqueueSnackbar({
                        key: String(new Date().getTime() + Math.random()),
                        message: 'Nur JPG, PNG, WEBP oder GIF-Dateien sind erlaubt',
                        options: { variant: 'error' },
                    })
                );
                e.target.value = '';
                return;
            }

            if (file.size > maxFileSize) {
                dispatch(
                    NotificationActionCreator.enqueueSnackbar({
                        key: String(new Date().getTime() + Math.random()),
                        message: 'Maximale Dateigröße ist 8MB',
                        options: { variant: 'error' },
                    })
                );
                e.target.value = '';
                return;
            }

            const reader = new FileReader();
            reader.onloadend = async () => {
                await dispatch(ChatActionCreator.sendMessage(dialogId, reader.result as string, ChatMessageTypes.ZWINKER, user!.Userid));
                onSend?.();
            };
            reader.readAsDataURL(file);
        }
    };

    const isDisabled = partnerId === 'support';

    return (
        <>
            <div className="chat-input-bar">
                {canSendIcebreaker && <IceBreakerIconButton Profilid={partnerId} Username={userName} onSend={onSend} />}

                <div className={`chat-input-pill ${type === 'dark' ? 'chat-input-pill--dark' : ''}`}>
                    <button type="button" className="chat-input-pill__btn" onClick={() => fileInputRef.current?.click()} disabled={isDisabled} aria-label="Attach">
                        <Paperclip strokeWidth={2} />
                    </button>
                    <input ref={fileInputRef} type="file" accept="image/*" hidden onChange={handleFileChange} />

                    <input
                        ref={inputRef}
                        className="chat-input-pill__field"
                        type="text"
                        placeholder={CHAT_INTERFACE_SEND_A_MESSAGE}
                        value={inputValue}
                        onChange={(e) => setInputValue(e.target.value)}
                        onKeyDown={handleEnter}
                        disabled={isDisabled}
                    />

                    {partnerId !== 'support' && (
                        <button type="button" className="chat-input-pill__btn" onClick={handleGiftsClick} aria-label="Gift">
                            <Gift strokeWidth={2} />
                        </button>
                    )}
                </div>

                <button type="button" className="chat-input-send" onClick={handler} disabled={isDisabled || !inputValue.trim()} aria-label="Send">
                    <Send strokeWidth={2.2} />
                </button>
            </div>

            <ChatGiftComponent
                open={Boolean(anchorEl)}
                anchorEl={anchorEl}
                onClose={handleGiftsClose}
                user={user}
                partnerId={partnerId}
                partnerUsername={userName}
                onSend={onSend}
            />
        </>
    );
});

export default ChatInput;
